
import React, { useState } from "react";
import { useTeacherData } from "@/hooks/useTeacherData";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { printTeachersListPaginatedAsJPG } from '@/utils/printing/teacherListPaginatedPrinter';
import { printTeachersListPaginatedAsPDF } from '@/utils/printing/teacherListPaginatedPrinterPDF';
import TeachersPageSelectionDialog from '@/components/teachers/TeachersPageSelectionDialog';
import TeachersFormatDialog from '@/components/teachers/TeachersFormatDialog';
import { enhancedToast } from '@/utils/notifications/enhancedToast';

const TeachersOverviewPage = () => {
  const { teachers, loading, filterText, setFilterText } = useTeacherData();
  const navigate = useNavigate();
  const [isPrinting, setIsPrinting] = useState(false);
  const [isPageDialogOpen, setIsPageDialogOpen] = useState(false);
  const [isFormatDialogOpen, setIsFormatDialogOpen] = useState(false);
  const [selectedPages, setSelectedPages] = useState<{ start: number; end: number } | null>(null);

  const handlePrintTeachersList = () => {
    if (!teachers || teachers.length === 0) {
      enhancedToast.error("No teachers available to download");
      return;
    }

    const teachersPerPage = 12;
    const totalPages = Math.ceil(teachers.length / teachersPerPage);

    // If only one page, show format dialog directly
    if (totalPages === 1) {
      setSelectedPages({ start: 1, end: 1 });
      setIsFormatDialogOpen(true);
    } else {
      // Show page selection dialog first
      setIsPageDialogOpen(true);
    }
  };

  const handlePageSelection = (startPage: number, endPage: number) => {
    setSelectedPages({ start: startPage, end: endPage });
    setIsPageDialogOpen(false);
    setIsFormatDialogOpen(true);
  };

  const handleFormatDownload = async (downloadFormat: 'jpg' | 'pdf') => {
    if (isPrinting) return;
    
    setIsPrinting(true);
    
    try {
      const teachersList = teachers.map(teacher => {
        // Safely handle qualifications array
        let qualifications: string[] = [];
        if (Array.isArray(teacher.qualifications)) {
          qualifications = teacher.qualifications.map(q => {
            if (typeof q === 'string') {
              return q;
            } else if (q && typeof q === 'object' && q.degree) {
              return q.degree;
            }
            return '';
          }).filter(q => q.length > 0);
        }

        return {
          id: teacher.id || '',
          name: `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim(),
          department: teacher.department || 'N/A',
          position: teacher.position || 'N/A',
          email: teacher.email || 'N/A',
          phone: teacher.phone || 'N/A',
          qualifications: qualifications,
          joinDate: teacher.joinDate ? format(new Date(teacher.joinDate), 'MMM dd, yyyy') : 'N/A',
          status: teacher.employmentType || 'Active'
        };
      });
      
      const startPage = selectedPages?.start || 1;
      const endPage = selectedPages?.end || Math.ceil(teachers.length / 12);
      
      if (downloadFormat === 'pdf') {
        await printTeachersListPaginatedAsPDF(teachersList, startPage, endPage);
      } else {
        await printTeachersListPaginatedAsJPG(teachersList, startPage, endPage);
      }
    } catch (error) {
      console.error('Error downloading teachers catalog:', error);
      enhancedToast.error("Failed to download teachers catalog");
    } finally {
      setIsPrinting(false);
      setIsFormatDialogOpen(false);
      setSelectedPages(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold tracking-tight">Teachers Overview</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
          <Button
            variant="outline"
            onClick={handlePrintTeachersList}
            className="flex items-center gap-2"
            disabled={isPrinting || !teachers || teachers.length === 0}
          >
            <Printer className="h-4 w-4" />
            {isPrinting ? 'Processing...' : 'Print Catalog'}
          </Button>
        </div>
      </div>

      <div className="rounded-md border bg-card">
        <div className="p-4">
          <Input
            placeholder="Search teachers by name, department or status..."
            className="max-w-full"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Qualifications</TableHead>
              <TableHead>Join Date</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center">Loading...</TableCell>
              </TableRow>
            ) : !teachers.length ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center">No teachers found</TableCell>
              </TableRow>
            ) : (
              teachers.map((teacher) => (
                <TableRow key={teacher.id}>
                  <TableCell className="font-medium">{`${teacher.firstName || ''} ${teacher.lastName || ''}`}</TableCell>
                  <TableCell>{teacher.department || 'N/A'}</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div>{teacher.email}</div>
                      <div className="text-sm text-muted-foreground">{teacher.phone || 'No phone'}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {teacher.qualifications?.map((qual, idx) => (
                        <Badge key={idx} variant="secondary">
                          {typeof qual === 'string' ? qual : qual?.degree}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {teacher.joinDate ? format(new Date(teacher.joinDate), 'MMM dd, yyyy') : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={teacher.employmentType === 'Full-time' ? 'default' : 'secondary'}>
                      {teacher.employmentType || 'Active'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Page Selection Dialog */}
      <TeachersPageSelectionDialog
        isOpen={isPageDialogOpen}
        onClose={() => setIsPageDialogOpen(false)}
        totalTeachers={teachers?.length || 0}
        onDownload={handlePageSelection}
      />

      {/* Format Selection Dialog */}
      <TeachersFormatDialog
        isOpen={isFormatDialogOpen}
        onClose={() => {
          setIsFormatDialogOpen(false);
          setSelectedPages(null);
        }}
        onDownload={handleFormatDownload}
        totalTeachers={teachers?.length || 0}
      />
    </div>
  );
};

export default TeachersOverviewPage;
